import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import debounce from 'lodash/debounce';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { USER_TYPING_DEBOUNCE_TIME } from '@atlassian/help-center-common-component/constants';
import { FieldBase } from '@atlassian/help-center-common-component/fields/field-base';
import AsyncRichTextEditorV2 from '@atlassian/help-center-common-component/fields/rich-text-editor-v2';
import type { FieldFormProps } from '@atlassian/help-center-common-component/fields/types';
import { FieldLoadingSkeleton } from '@atlassian/help-center-common-component/loading';
import type { FieldConfigs } from '@atlassian/help-center-common-component/mention-provider';
import type { RichTextAreaWrapperProps } from '@atlassian/help-center-common-component/rich-text-area-wrapper';
import { getRichTextAreaWrapperStyles } from '@atlassian/help-center-common-component/rich-text-area-wrapper';
import { isOnlyWhitespaceAdf } from '@atlassian/help-center-common-util/adf';

interface Props extends FieldFormProps<string> {
    defaultValue?: string;
    fieldConfigs?: FieldConfigs;
}

class RichTextArea extends React.Component<Props> {
    onFieldChange = (transformedDoc: JSONDocNode): void => {
        const { id, onChange } = this.props;
        if (onChange) {
            // When the fields content is cleared we reset it's value to an empty string in order to correctly display the dirty form warning
            onChange(id, isOnlyWhitespaceAdf(transformedDoc) ? '' : JSON.stringify(transformedDoc));
        }
    };

    onFieldChangeDebounced = debounce(this.onFieldChange, USER_TYPING_DEBOUNCE_TIME);

    render() {
        const { id, label, required, errorMessage, description, disabled, defaultValue, fieldConfigs } = this.props;
        return (
            <FieldBase
                label={label}
                description={description}
                htmlFor={id}
                required={required}
                errorMessage={errorMessage}
            >
                <RichTextAreaWrapper id={id} hasError={Boolean(errorMessage)} data-test-id="rich-text-area-field">
                    <AsyncRichTextEditorV2
                        isExpanded
                        editorType="field"
                        isEmojiEnabled
                        disabled={!!disabled}
                        onChange={this.onFieldChangeDebounced}
                        defaultValue={defaultValue}
                        fieldConfigs={fieldConfigs}
                        LoadingComponent={FieldLoadingSkeleton}
                    />
                </RichTextAreaWrapper>
            </FieldBase>
        );
    }
}

export default RichTextArea;

// Manual overrides required to theme the editor to look like a request create form field
// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const RichTextAreaWrapper = styled.div<RichTextAreaWrapperProps>`
    flex: 1 1 100%;
    ${getRichTextAreaWrapperStyles()}
`;
